import React, { useState } from 'react';
import { 
  Users, 
  TrendingUp, 
  DollarSign, 
  ShoppingCart,
  BarChart3,
  Calendar,
  Filter,
  Eye,
  Download,
  RefreshCw,
  TrendingDown,
  FileText,
  Printer,
  X,
  Package,
  User,
  Star,
  Clock,
  Tag,
  MapPin,
  Phone,
  Mail
} from 'lucide-react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

// Custom CSS for larger date picker
const customDatePickerStyles = `
  .react-datepicker {
    font-size: 16px !important;
    width: 320px !important;
  }
  .react-datepicker__header {
    padding: 20px !important;
  }
  .react-datepicker__current-month {
    font-size: 18px !important;
    font-weight: bold !important;
  }
  .react-datepicker__day-name {
    font-size: 14px !important;
    padding: 8px !important;
  }
  .react-datepicker__day {
    font-size: 16px !important;
    padding: 10px !important;
    margin: 2px !important;
  }
  .react-datepicker__day:hover {
    background-color: #3b82f6 !important;
    color: white !important;
  }
  .react-datepicker__day--selected {
    background-color: #3b82f6 !important;
    color: white !important;
  }
  .react-datepicker__day--in-range {
    background-color: #dbeafe !important;
    color: #1e40af !important;
  }
  .react-datepicker__day--keyboard-selected {
    background-color: #3b82f6 !important;
    color: white !important;
  }
  .react-datepicker__navigation {
    top: 20px !important;
  }
  .react-datepicker__navigation-icon {
    font-size: 18px !important;
  }
  
  /* Custom CSS for chart optimization on small laptop screens */
  @media (max-width: 1366px) {
    .chart-container {
      height: 400px !important;
    }
  }
  
  @media (max-width: 1024px) {
    .chart-container {
      height: 350px !important;
    }
  }
  
  @media (max-width: 768px) {
    .chart-container {
      height: 300px !important;
    }
  }
  
  /* Optimize chart responsiveness */
  .chart-wrapper {
    position: relative;
    width: 100%;
    height: 100%;
    min-height: 300px;
  }
  
  /* Ensure chart maintains aspect ratio on small screens */
  @media (max-width: 640px) {
    .chart-container {
      height: 250px !important;
    }
  }
  
  /* Optimize for small laptop screens (1366px and below) */
  @media (max-width: 1366px) {
    .dashboard-container {
      max-width: 100% !important;
      padding: 0.5rem !important;
    }
    
    .chart-section {
      margin-bottom: 1rem !important;
    }
    
    .stats-cards {
      grid-template-columns: repeat(2, 1fr) !important;
      gap: 0.5rem !important;
    }
    
    .secondary-charts {
      grid-template-columns: 1fr !important;
      gap: 1rem !important;
    }
  }
  
  /* Fullscreen chart optimization */
  .chart-fullscreen {
    position: fixed !important;
    top: 0 !important;
    left: 0 !important;
    width: 100vw !important;
    height: 100vh !important;
    z-index: 9999 !important;
    background: white !important;
    padding: 1rem !important;
  }
`;

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

const AdminDashboard = () => {
  const [timeRange, setTimeRange] = useState('month');
  const [selectedChart, setSelectedChart] = useState('users');
  const [revenueTimeRange, setRevenueTimeRange] = useState('month');
  const [ordersTimeRange, setOrdersTimeRange] = useState('month');
  const [profitTimeRange, setProfitTimeRange] = useState('month');
  
  // Custom date range states
  const [startDate, setStartDate] = useState(new Date(new Date().getTime() - 30 * 24 * 60 * 60 * 1000));
  const [endDate, setEndDate] = useState(new Date());
  const [showCustomDatePicker, setShowCustomDatePicker] = useState(false);
  
  // Export state
  const [isExporting, setIsExporting] = useState(false);
  
  // Popup states
  const [showProductPopup, setShowProductPopup] = useState(false);
  const [showSellerPopup, setShowSellerPopup] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [selectedSeller, setSelectedSeller] = useState(null);
  const [isChartFullscreen, setIsChartFullscreen] = useState(false);
  
  // Date range validation
  const validateDateRange = (start, end) => {
    const diffTime = Math.abs(end - start);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays <= 31; // Maximum 31 days
  };

  // Handle time range change
  const handleTimeRangeChange = (newTimeRange) => {
    setTimeRange(newTimeRange);
    setRevenueTimeRange(newTimeRange);
    setOrdersTimeRange(newTimeRange);
    setProfitTimeRange(newTimeRange);
    setShowCustomDatePicker(false);
  };

  // Handle custom date range
  const handleCustomDateRange = () => {
    if (validateDateRange(startDate, endDate)) {
      setTimeRange('custom');
      setRevenueTimeRange('custom');
      setOrdersTimeRange('custom');
      setProfitTimeRange('custom');
      setShowCustomDatePicker(false);
    } else {
      alert('Khoảng thời gian không được vượt quá 31 ngày!');
    }
  };

  // Export report function
  const exportReport = async () => {
    setIsExporting(true);
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      // Create report data
      const reportData = {
        timeRange: timeRange,
        startDate: startDate,
        endDate: endDate,
        stats: getStatsByTimeRange(timeRange),
        revenueData: getCurrentRevenueData(),
        ordersData: getCurrentOrdersData(),
        profitData: getCurrentProfitData(),
        topProducts: getTopProductsByTimeRange(timeRange),
        topSellers: getTopSellersByTimeRange(timeRange),
        exportDate: new Date().toISOString()
      };

      // Create CSV content
      const csvContent = generateCSVReport(reportData);
      
      // Download file
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `bao-cao-dashboard-${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      alert('Xuất báo cáo thành công!');
    } catch (error) {
      alert('Có lỗi xảy ra khi xuất báo cáo!');
    } finally {
      setIsExporting(false);
    }
  };

  // Handle product click
  const handleProductClick = (product) => {
    setSelectedProduct(product);
    setShowProductPopup(true);
  };

  // Handle seller click
  const handleSellerClick = (seller) => {
    setSelectedSeller(seller);
    setShowSellerPopup(true);
  };

  // Close popups
  const closeProductPopup = () => {
    setShowProductPopup(false);
    setSelectedProduct(null);
  };

  const closeSellerPopup = () => {
    setShowSellerPopup(false);
    setSelectedSeller(null);
  };

  // Generate CSV report
  const generateCSVReport = (data) => {
    let csv = 'Báo cáo Dashboard\n\n';
    csv += `Thời gian: ${timeRange === 'custom' ? `${startDate.toLocaleDateString()} - ${endDate.toLocaleDateString()}` : timeRange}\n`;
    csv += `Ngày xuất: ${new Date().toLocaleDateString()}\n\n`;
    
    // Stats
    csv += 'Thống kê tổng quan\n';
    csv += 'Chỉ số,Giá trị\n';
    csv += `Tổng người dùng,${data.stats.totalUsers}\n`;
    csv += `Người dùng mới,${data.stats.newUsers}\n`;
    csv += `Doanh thu,${formatCurrency(data.stats.revenue)}\n`;
    csv += `Chi phí,${formatCurrency(data.stats.expenses)}\n`;
    csv += `Lợi nhuận,${formatCurrency(data.stats.profit)}\n`;
    csv += `Đơn hàng,${data.stats.orders}\n`;
    csv += `Tăng trưởng,${data.stats.growth}%\n\n`;
    
    // Revenue data
    csv += 'Dữ liệu doanh thu\n';
    csv += 'Thời gian,Doanh thu,Tăng trưởng\n';
    data.revenueData.forEach(item => {
      csv += `${getTimeLabel(item, 'revenue')},${formatCurrency(item.revenue)},${item.growth}%\n`;
    });
    csv += '\n';
    
    // Orders data
    csv += 'Dữ liệu đơn hàng\n';
    csv += 'Thời gian,Đơn hàng,Tăng trưởng\n';
    data.ordersData.forEach(item => {
      csv += `${getTimeLabel(item, 'orders')},${item.orders},${item.growth}%\n`;
    });
    csv += '\n';
    
    // Profit data
    csv += 'Dữ liệu lợi nhuận\n';
    csv += 'Thời gian,Lợi nhuận,Tăng trưởng\n';
    data.profitData.forEach(item => {
      csv += `${getTimeLabel(item, 'profit')},${formatCurrency(item.profit)},${item.growth}%\n`;
    });
    csv += '\n';
    
    // Top products
    csv += 'Top sản phẩm bán chạy\n';
    csv += 'Tên sản phẩm,Đã bán,Doanh thu,Tăng trưởng\n';
    data.topProducts.forEach(product => {
      csv += `${product.name},${product.sales},${formatCurrency(product.revenue)},${product.growth}%\n`;
    });
    csv += '\n';
    
    // Top sellers
    csv += 'Top seller doanh thu cao\n';
    csv += 'Tên seller,Đơn hàng,Doanh thu,Tăng trưởng\n';
    data.topSellers.forEach(seller => {
      csv += `${seller.name},${seller.orders},${formatCurrency(seller.revenue)},${seller.growth}%\n`;
    });
    
    return csv;
  };

  // Mock data - sau này sẽ lấy từ API
  const getStatsByTimeRange = (timeRange) => {
    const statsData = {
      daily: {
    totalUsers: 1250,
        newUsers: 15,
    activeUsers: 890,
        revenue: 500000,
        expenses: 350000,
        profit: 150000,
        orders: 25,
        growth: 8.5
      },
      weekly: {
        totalUsers: 1280,
        newUsers: 30,
        activeUsers: 920,
        revenue: 3500000,
        expenses: 2400000,
        profit: 1100000,
        orders: 85,
        growth: 12.3
      },
      monthly: {
        totalUsers: 1350,
        newUsers: 100,
        activeUsers: 980,
        revenue: 15000000,
        expenses: 10500000,
        profit: 4500000,
        orders: 350,
        growth: 15.7
      },
      yearly: {
        totalUsers: 1800,
        newUsers: 550,
        activeUsers: 1200,
        revenue: 180000000,
        expenses: 126000000,
        profit: 54000000,
        orders: 4200,
        growth: 22.8
      },
      custom: {
        totalUsers: 1300,
        newUsers: 50,
        activeUsers: 950,
        revenue: 2500000,
        expenses: 1750000,
        profit: 750000,
        orders: 125,
        growth: 10.2
      }
    };
    
    return statsData[timeRange] || statsData.monthly;
  };

  const chartData = [
    { month: 'T1', users: 120, revenue: 1000000, orders: 25, profit: 300000 },
    { month: 'T2', users: 150, revenue: 1200000, orders: 30, profit: 350000 },
    { month: 'T3', users: 180, revenue: 1500000, orders: 35, profit: 400000 },
    { month: 'T4', users: 200, revenue: 1800000, orders: 40, profit: 450000 },
    { month: 'T5', users: 220, revenue: 2000000, orders: 45, profit: 500000 },
    { month: 'T6', users: 250, revenue: 2200000, orders: 50, profit: 550000 }
  ];

  // Revenue data theo thời gian
  const revenueData = {
    daily: [
      { date: '01/01', revenue: 50000, growth: 5.2 },
      { date: '02/01', revenue: 55000, growth: 10.0 },
      { date: '03/01', revenue: 48000, growth: -12.7 },
      { date: '04/01', revenue: 62000, growth: 29.2 },
      { date: '05/01', revenue: 58000, growth: -6.5 },
      { date: '06/01', revenue: 65000, growth: 12.1 },
      { date: '07/01', revenue: 70000, growth: 7.7 }
    ],
    custom: [
      { date: '01/01', revenue: 50000, growth: 5.2 },
      { date: '02/01', revenue: 55000, growth: 10.0 },
      { date: '03/01', revenue: 48000, growth: -12.7 },
      { date: '04/01', revenue: 62000, growth: 29.2 },
      { date: '05/01', revenue: 58000, growth: -6.5 },
      { date: '06/01', revenue: 65000, growth: 12.1 },
      { date: '07/01', revenue: 70000, growth: 7.7 },
      { date: '08/01', revenue: 75000, growth: 7.1 },
      { date: '09/01', revenue: 68000, growth: -9.3 },
      { date: '10/01', revenue: 82000, growth: 20.6 },
      { date: '11/01', revenue: 78000, growth: -4.9 },
      { date: '12/01', revenue: 85000, growth: 9.0 },
      { date: '13/01', revenue: 92000, growth: 8.2 },
      { date: '14/01', revenue: 88000, growth: -4.3 },
      { date: '15/01', revenue: 95000, growth: 8.0 },
      { date: '16/01', revenue: 102000, growth: 7.4 },
      { date: '17/01', revenue: 98000, growth: -3.9 },
      { date: '18/01', revenue: 105000, growth: 7.1 },
      { date: '19/01', revenue: 112000, growth: 6.7 },
      { date: '20/01', revenue: 108000, growth: -3.6 },
      { date: '21/01', revenue: 115000, growth: 6.5 },
      { date: '22/01', revenue: 122000, growth: 6.1 },
      { date: '23/01', revenue: 118000, growth: -3.3 },
      { date: '24/01', revenue: 125000, growth: 5.9 },
      { date: '25/01', revenue: 132000, growth: 5.6 },
      { date: '26/01', revenue: 128000, growth: -3.0 },
      { date: '27/01', revenue: 135000, growth: 5.5 },
      { date: '28/01', revenue: 142000, growth: 5.2 },
      { date: '29/01', revenue: 138000, growth: -2.8 },
      { date: '30/01', revenue: 145000, growth: 5.1 },
      { date: '31/01', revenue: 152000, growth: 4.8 }
    ],
    weekly: [
      { week: 'Tuần 1', revenue: 350000, growth: 8.5 },
      { week: 'Tuần 2', revenue: 420000, growth: 20.0 },
      { week: 'Tuần 3', revenue: 380000, growth: -9.5 },
      { week: 'Tuần 4', revenue: 450000, growth: 18.4 }
    ],
    monthly: [
      { month: 'T1', revenue: 1500000, growth: 8.5 },
      { month: 'T2', revenue: 1800000, growth: 20.0 },
      { month: 'T3', revenue: 2200000, growth: 22.2 },
      { month: 'T4', revenue: 2500000, growth: 13.6 },
      { month: 'T5', revenue: 2800000, growth: 12.0 },
      { month: 'T6', revenue: 3200000, growth: 14.3 }
    ],
    yearly: [
      { year: '2020', revenue: 15000000, growth: 0 },
      { year: '2021', revenue: 18000000, growth: 20.0 },
      { year: '2022', revenue: 22000000, growth: 22.2 },
      { year: '2023', revenue: 28000000, growth: 27.3 },
      { year: '2024', revenue: 35000000, growth: 25.0 }
    ]
  };

  // Orders data theo thời gian
  const ordersData = {
    daily: [
      { date: '01/01', orders: 12, growth: 8.3 },
      { date: '02/01', orders: 15, growth: 25.0 },
      { date: '03/01', orders: 11, growth: -26.7 },
      { date: '04/01', orders: 18, growth: 63.6 },
      { date: '05/01', orders: 14, growth: -22.2 },
      { date: '06/01', orders: 16, growth: 14.3 },
      { date: '07/01', orders: 19, growth: 18.8 }
    ],
    custom: [
      { date: '01/01', orders: 12, growth: 8.3 },
      { date: '02/01', orders: 15, growth: 25.0 },
      { date: '03/01', orders: 11, growth: -26.7 },
      { date: '04/01', orders: 18, growth: 63.6 },
      { date: '05/01', orders: 14, growth: -22.2 },
      { date: '06/01', orders: 16, growth: 14.3 },
      { date: '07/01', orders: 19, growth: 18.8 },
      { date: '08/01', orders: 22, growth: 15.8 },
      { date: '09/01', orders: 18, growth: -18.2 },
      { date: '10/01', orders: 25, growth: 38.9 },
      { date: '11/01', orders: 21, growth: -16.0 },
      { date: '12/01', orders: 28, growth: 33.3 },
      { date: '13/01', orders: 32, growth: 14.3 },
      { date: '14/01', orders: 29, growth: -9.4 },
      { date: '15/01', orders: 35, growth: 20.7 },
      { date: '16/01', orders: 38, growth: 8.6 },
      { date: '17/01', orders: 34, growth: -10.5 },
      { date: '18/01', orders: 41, growth: 20.6 },
      { date: '19/01', orders: 45, growth: 9.8 },
      { date: '20/01', orders: 41, growth: -8.9 },
      { date: '21/01', orders: 48, growth: 17.1 },
      { date: '22/01', orders: 52, growth: 8.3 },
      { date: '23/01', orders: 48, growth: -7.7 },
      { date: '24/01', orders: 55, growth: 14.6 },
      { date: '25/01', orders: 59, growth: 7.3 },
      { date: '26/01', orders: 55, growth: -6.8 },
      { date: '27/01', orders: 62, growth: 12.7 },
      { date: '28/01', orders: 66, growth: 6.5 },
      { date: '29/01', orders: 62, growth: -6.1 },
      { date: '30/01', orders: 69, growth: 11.3 },
      { date: '31/01', orders: 73, growth: 5.8 }
    ],
    weekly: [
      { week: 'Tuần 1', orders: 85, growth: 12.5 },
      { week: 'Tuần 2', orders: 95, growth: 11.8 },
      { week: 'Tuần 3', orders: 88, growth: -7.4 },
      { week: 'Tuần 4', orders: 102, growth: 15.9 }
    ],
    monthly: [
      { month: 'T1', orders: 350, growth: 10.5 },
      { month: 'T2', orders: 420, growth: 20.0 },
      { month: 'T3', orders: 480, growth: 14.3 },
      { month: 'T4', orders: 520, growth: 8.3 },
      { month: 'T5', orders: 580, growth: 11.5 },
      { month: 'T6', orders: 650, growth: 12.1 }
    ],
    yearly: [
      { year: '2020', orders: 4200, growth: 0 },
      { year: '2021', orders: 5200, growth: 23.8 },
      { year: '2022', orders: 6800, growth: 30.8 },
      { year: '2023', orders: 8500, growth: 25.0 },
      { year: '2024', orders: 10200, growth: 20.0 }
    ]
  };

  // Profit data theo thời gian
  const profitData = {
    daily: [
      { date: '01/01', profit: 15000, growth: 6.7 },
      { date: '02/01', profit: 16500, growth: 10.0 },
      { date: '03/01', profit: 14400, growth: -12.7 },
      { date: '04/01', profit: 18600, growth: 29.2 },
      { date: '05/01', profit: 17400, growth: -6.5 },
      { date: '06/01', profit: 19500, growth: 12.1 },
      { date: '07/01', profit: 21000, growth: 7.7 }
    ],
    custom: [
      { date: '01/01', profit: 15000, growth: 6.7 },
      { date: '02/01', profit: 16500, growth: 10.0 },
      { date: '03/01', profit: 14400, growth: -12.7 },
      { date: '04/01', profit: 18600, growth: 29.2 },
      { date: '05/01', profit: 17400, growth: -6.5 },
      { date: '06/01', profit: 19500, growth: 12.1 },
      { date: '07/01', profit: 21000, growth: 7.7 },
      { date: '08/01', profit: 22500, growth: 7.1 },
      { date: '09/01', profit: 20400, growth: -9.3 },
      { date: '10/01', profit: 24600, growth: 20.6 },
      { date: '11/01', profit: 23400, growth: -4.9 },
      { date: '12/01', profit: 25500, growth: 9.0 },
      { date: '13/01', profit: 27600, growth: 8.2 },
      { date: '14/01', profit: 26400, growth: -4.3 },
      { date: '15/01', profit: 28500, growth: 8.0 },
      { date: '16/01', profit: 30600, growth: 7.4 },
      { date: '17/01', profit: 29400, growth: -3.9 },
      { date: '18/01', profit: 31500, growth: 7.1 },
      { date: '19/01', profit: 33600, growth: 6.7 },
      { date: '20/01', profit: 32400, growth: -3.6 },
      { date: '21/01', profit: 34500, growth: 6.5 },
      { date: '22/01', profit: 36600, growth: 6.1 },
      { date: '23/01', profit: 35400, growth: -3.3 },
      { date: '24/01', profit: 37500, growth: 5.9 },
      { date: '25/01', profit: 39600, growth: 5.6 },
      { date: '26/01', profit: 38400, growth: -3.0 },
      { date: '27/01', profit: 40500, growth: 5.5 },
      { date: '28/01', profit: 42600, growth: 5.2 },
      { date: '29/01', profit: 41400, growth: -2.8 },
      { date: '30/01', profit: 43500, growth: 5.1 },
      { date: '31/01', profit: 45600, growth: 4.8 }
    ],
    weekly: [
      { week: 'Tuần 1', profit: 105000, growth: 9.5 },
      { week: 'Tuần 2', profit: 126000, growth: 20.0 },
      { week: 'Tuần 3', profit: 114000, growth: -9.5 },
      { week: 'Tuần 4', profit: 135000, growth: 18.4 }
    ],
    monthly: [
      { month: 'T1', profit: 450000, growth: 8.5 },
      { month: 'T2', profit: 540000, growth: 20.0 },
      { month: 'T3', profit: 660000, growth: 22.2 },
      { month: 'T4', profit: 750000, growth: 13.6 },
      { month: 'T5', profit: 840000, growth: 12.0 },
      { month: 'T6', profit: 960000, growth: 14.3 }
    ],
    yearly: [
      { year: '2020', profit: 4500000, growth: 0 },
      { year: '2021', profit: 5400000, growth: 20.0 },
      { year: '2022', profit: 6600000, growth: 22.2 },
      { year: '2023', profit: 8400000, growth: 27.3 },
      { year: '2024', profit: 10500000, growth: 25.0 }
    ]
  };

  const getTopProductsByTimeRange = (timeRange) => {
    const productsData = {
      daily: [
        { name: 'Sản phẩm A', sales: 15, revenue: 250000, growth: 15.2 },
        { name: 'Sản phẩm B', sales: 12, revenue: 200000, growth: 8.5 },
        { name: 'Sản phẩm C', sales: 10, revenue: 180000, growth: 12.3 },
        { name: 'Sản phẩm D', sales: 9, revenue: 150000, growth: -2.1 },
        { name: 'Sản phẩm E', sales: 8, revenue: 120000, growth: 5.7 }
      ],
      weekly: [
        { name: 'Sản phẩm A', sales: 85, revenue: 1500000, growth: 15.2 },
        { name: 'Sản phẩm B', sales: 68, revenue: 1200000, growth: 8.5 },
        { name: 'Sản phẩm C', sales: 55, revenue: 1000000, growth: 12.3 },
        { name: 'Sản phẩm D', sales: 45, revenue: 800000, growth: -2.1 },
        { name: 'Sản phẩm E', sales: 40, revenue: 650000, growth: 5.7 }
      ],
      monthly: [
        { name: 'Sản phẩm A', sales: 350, revenue: 6000000, growth: 15.2 },
        { name: 'Sản phẩm B', sales: 280, revenue: 4800000, growth: 8.5 },
        { name: 'Sản phẩm C', sales: 220, revenue: 4000000, growth: 12.3 },
        { name: 'Sản phẩm D', sales: 180, revenue: 3200000, growth: -2.1 },
        { name: 'Sản phẩm E', sales: 160, revenue: 2800000, growth: 5.7 }
      ],
      yearly: [
        { name: 'Sản phẩm A', sales: 4200, revenue: 72000000, growth: 15.2 },
        { name: 'Sản phẩm B', sales: 3360, revenue: 57600000, growth: 8.5 },
        { name: 'Sản phẩm C', sales: 2640, revenue: 48000000, growth: 12.3 },
        { name: 'Sản phẩm D', sales: 2160, revenue: 38400000, growth: -2.1 },
        { name: 'Sản phẩm E', sales: 1920, revenue: 33600000, growth: 5.7 }
      ],
      custom: [
        { name: 'Sản phẩm A', sales: 125, revenue: 2100000, growth: 15.2 },
        { name: 'Sản phẩm B', sales: 100, revenue: 1680000, growth: 8.5 },
        { name: 'Sản phẩm C', sales: 80, revenue: 1440000, growth: 12.3 },
        { name: 'Sản phẩm D', sales: 65, revenue: 1150000, growth: -2.1 },
        { name: 'Sản phẩm E', sales: 58, revenue: 1000000, growth: 5.7 }
      ]
    };
    
    return productsData[timeRange] || productsData.monthly;
  };

  const getTopSellersByTimeRange = (timeRange) => {
    const sellersData = {
      daily: [
        { name: 'Nguyễn Văn A', orders: 8, revenue: 350000, growth: 18.5 },
        { name: 'Trần Thị B', orders: 6, revenue: 280000, growth: 12.3 },
        { name: 'Lê Văn C', orders: 5, revenue: 240000, growth: -5.2 },
        { name: 'Phạm Thị D', orders: 4, revenue: 210000, growth: 8.7 },
        { name: 'Hoàng Văn E', orders: 3, revenue: 180000, growth: 3.4 }
      ],
      weekly: [
        { name: 'Nguyễn Văn A', orders: 35, revenue: 1500000, growth: 18.5 },
        { name: 'Trần Thị B', orders: 28, revenue: 1200000, growth: 12.3 },
        { name: 'Lê Văn C', orders: 22, revenue: 1000000, growth: -5.2 },
        { name: 'Phạm Thị D', orders: 18, revenue: 800000, growth: 8.7 },
        { name: 'Hoàng Văn E', orders: 15, revenue: 650000, growth: 3.4 }
      ],
      monthly: [
        { name: 'Nguyễn Văn A', orders: 150, revenue: 6500000, growth: 18.5 },
        { name: 'Trần Thị B', orders: 120, revenue: 5200000, growth: 12.3 },
        { name: 'Lê Văn C', orders: 95, revenue: 4200000, growth: -5.2 },
        { name: 'Phạm Thị D', orders: 80, revenue: 3500000, growth: 8.7 },
        { name: 'Hoàng Văn E', orders: 65, revenue: 2800000, growth: 3.4 }
      ],
      yearly: [
        { name: 'Nguyễn Văn A', orders: 1800, revenue: 78000000, growth: 18.5 },
        { name: 'Trần Thị B', orders: 1440, revenue: 62400000, growth: 12.3 },
        { name: 'Lê Văn C', orders: 1140, revenue: 50400000, growth: -5.2 },
        { name: 'Phạm Thị D', orders: 960, revenue: 42000000, growth: 8.7 },
        { name: 'Hoàng Văn E', orders: 780, revenue: 33600000, growth: 3.4 }
      ],
      custom: [
        { name: 'Nguyễn Văn A', orders: 45, revenue: 1950000, growth: 18.5 },
        { name: 'Trần Thị B', orders: 36, revenue: 1560000, growth: 12.3 },
        { name: 'Lê Văn C', orders: 28, revenue: 1260000, growth: -5.2 },
        { name: 'Phạm Thị D', orders: 24, revenue: 1050000, growth: 8.7 },
        { name: 'Hoàng Văn E', orders: 20, revenue: 840000, growth: 3.4 }
      ]
    };
    
    return sellersData[timeRange] || sellersData.monthly;
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('vi-VN', {
      style: 'currency',
      currency: 'VND'
    }).format(amount);
  };

  const getGrowthIcon = (growth) => {
    if (growth > 0) {
      return <TrendingUp className="w-4 h-4 text-green-600" />;
    } else if (growth < 0) {
      return <TrendingDown className="w-4 h-4 text-red-600" />;
    }
    return <BarChart3 className="w-4 h-4 text-gray-600" />;
  };

  const getGrowthColor = (growth) => {
    if (growth > 0) return 'text-green-600';
    if (growth < 0) return 'text-red-600';
    return 'text-gray-600';
  };

  const getCurrentRevenueData = () => {
    switch (revenueTimeRange) {
      case 'daily':
        return revenueData.daily;
      case 'weekly':
        return revenueData.weekly;
      case 'monthly':
        return revenueData.monthly;
      case 'yearly':
        return revenueData.yearly;
      case 'custom':
        return revenueData.custom;
      default:
        return revenueData.monthly;
    }
  };

  const getCurrentOrdersData = () => {
    switch (ordersTimeRange) {
      case 'daily':
        return ordersData.daily;
      case 'weekly':
        return ordersData.weekly;
      case 'monthly':
        return ordersData.monthly;
      case 'yearly':
        return ordersData.yearly;
      case 'custom':
        return ordersData.custom;
      default:
        return ordersData.monthly;
    }
  };

  const getCurrentProfitData = () => {
    switch (profitTimeRange) {
      case 'daily':
        return profitData.daily;
      case 'weekly':
        return profitData.weekly;
      case 'monthly':
        return profitData.monthly;
      case 'yearly':
        return profitData.yearly;
      case 'custom':
        return profitData.custom;
      default:
        return profitData.monthly;
    }
  };

  const getTimeLabel = (item, type) => {
    switch (type) {
      case 'revenue':
        switch (revenueTimeRange) {
          case 'daily':
          case 'custom':
            return item.date;
          case 'weekly':
            return item.week;
          case 'monthly':
            return item.month;
          case 'yearly':
            return item.year;
          default:
            return item.month;
        }
      case 'orders':
        switch (ordersTimeRange) {
          case 'daily':
          case 'custom':
            return item.date;
          case 'weekly':
            return item.week;
          case 'monthly':
            return item.month;
          case 'yearly':
            return item.year;
          default:
            return item.month;
        }
      case 'profit':
        switch (profitTimeRange) {
          case 'daily':
          case 'custom':
            return item.date;
          case 'weekly':
            return item.week;
          case 'monthly':
            return item.month;
          case 'yearly':
            return item.year;
          default:
            return item.month;
        }
      default:
        return item.month;
    }
  };

  const getMaxValue = (data, field) => {
    return Math.max(...data.map(item => item[field]));
  };

  // Base chart configuration
  const baseChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
    },
    scales: {
      x: {
        grid: {
          display: false,
        },
        ticks: {
          color: '#6B7280',
          font: {
            size: 12,
          },
        },
      },
      y: {
        grid: {
          color: '#E5E7EB',
          borderDash: [5, 5],
        },
        ticks: {
          color: '#6B7280',
          font: {
            size: 12,
          },
        },
      },
    },
    interaction: {
      mode: 'nearest',
      axis: 'x',
      intersect: false,
    },
  };

  // Revenue chart options
  const revenueChartOptions = {
    ...baseChartOptions,
    plugins: {
      ...baseChartOptions.plugins,
      tooltip: {
        mode: 'index',
        intersect: false,
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        titleColor: '#fff',
        bodyColor: '#fff',
        borderColor: '#fff',
        borderWidth: 1,
        callbacks: {
          label: function(context) {
            let label = context.dataset.label || '';
            if (label) {
              label += ': ';
            }
            if (context.parsed.y !== null) {
              label += formatCurrency(context.parsed.y);
            }
            return label;
          }
        }
      },
    },
  };

  // Orders chart options
  const ordersChartOptions = {
    ...baseChartOptions,
    plugins: {
      ...baseChartOptions.plugins,
      tooltip: {
        mode: 'index',
        intersect: false,
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        titleColor: '#fff',
        bodyColor: '#fff',
        borderColor: '#fff',
        borderWidth: 1,
        callbacks: {
          label: function(context) {
            let label = context.dataset.label || '';
            if (label) {
              label += ': ';
            }
            if (context.parsed.y !== null) {
              label += context.parsed.y.toLocaleString() + ' đơn hàng';
            }
            return label;
          }
        }
      },
    },
  };

  // Profit chart options
  const profitChartOptions = {
    ...baseChartOptions,
    plugins: {
      ...baseChartOptions.plugins,
      tooltip: {
        mode: 'index',
        intersect: false,
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        titleColor: '#fff',
        bodyColor: '#fff',
        borderColor: '#fff',
        borderWidth: 1,
        callbacks: {
          label: function(context) {
            let label = context.dataset.label || '';
            if (label) {
              label += ': ';
            }
            if (context.parsed.y !== null) {
              label += formatCurrency(context.parsed.y);
            }
            return label;
          }
        }
      },
    },
  };

  // Combined chart options with dual Y-axis
  const combinedChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: true,
        position: 'top',
        labels: {
          usePointStyle: true,
          padding: 15,
          font: {
            size: 11,
          },
          boxWidth: 8,
          boxHeight: 8,
        },
      },
      tooltip: {
        mode: 'index',
        intersect: false,
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        titleColor: '#fff',
        bodyColor: '#fff',
        borderColor: '#fff',
        borderWidth: 1,
        titleFont: {
          size: 12,
        },
        bodyFont: {
          size: 11,
        },
        callbacks: {
          label: function(context) {
            let label = context.dataset.label || '';
            if (label) {
              label += ': ';
            }
            if (context.parsed.y !== null) {
              if (context.dataset.label === 'Đơn hàng') {
                label += context.parsed.y.toLocaleString() + ' đơn hàng';
              } else {
                label += formatCurrency(context.parsed.y);
              }
            }
            return label;
          }
        }
      },
    },
    scales: {
      x: {
        grid: {
          display: false,
        },
        ticks: {
          color: '#6B7280',
          font: {
            size: 10,
          },
          maxRotation: 45,
          minRotation: 0,
        },
      },
      y: {
        type: 'linear',
        display: true,
        position: 'left',
        grid: {
          color: '#E5E7EB',
          borderDash: [5, 5],
        },
        ticks: {
          color: '#6B7280',
          font: {
            size: 10,
          },
          callback: function(value) {
            return formatCurrency(value);
          }
        },
        title: {
          display: true,
          text: 'Doanh thu & Lợi nhuận (VND)',
          color: '#6B7280',
          font: {
            size: 10,
          },
        },
      },
      y1: {
        type: 'linear',
        display: true,
        position: 'right',
        grid: {
          drawOnChartArea: false,
        },
        ticks: {
          color: '#6B7280',
          font: {
            size: 10,
          },
        },
        title: {
          display: true,
          text: 'Đơn hàng',
          color: '#6B7280',
          font: {
            size: 10,
          },
        },
      },
    },
    interaction: {
      mode: 'nearest',
      axis: 'x',
      intersect: false,
    },
    elements: {
      point: {
        radius: 3,
        hoverRadius: 5,
      },
      line: {
        borderWidth: 2,
      },
    },
  };

  // Create chart data for revenue
  const getRevenueChartData = () => {
    const data = getCurrentRevenueData();
    return {
      labels: data.map(item => getTimeLabel(item, 'revenue')),
      datasets: [
        {
          label: 'Doanh thu',
          data: data.map(item => item.revenue),
          borderColor: '#10B981',
          backgroundColor: 'rgba(16, 185, 129, 0.1)',
          borderWidth: 3,
          fill: true,
          tension: 0.4,
          pointBackgroundColor: '#10B981',
          pointBorderColor: '#fff',
          pointBorderWidth: 2,
          pointRadius: 6,
          pointHoverRadius: 8,
        },
      ],
    };
  };

  // Create chart data for orders
  const getOrdersChartData = () => {
    const data = getCurrentOrdersData();
    return {
      labels: data.map(item => getTimeLabel(item, 'orders')),
      datasets: [
        {
          label: 'Đơn hàng',
          data: data.map(item => item.orders),
          borderColor: '#8B5CF6',
          backgroundColor: 'rgba(139, 92, 246, 0.1)',
          borderWidth: 3,
          fill: true,
          tension: 0.4,
          pointBackgroundColor: '#8B5CF6',
          pointBorderColor: '#fff',
          pointBorderWidth: 2,
          pointRadius: 6,
          pointHoverRadius: 8,
        },
      ],
    };
  };

  // Create chart data for profit
  const getProfitChartData = () => {
    const data = getCurrentProfitData();
    return {
      labels: data.map(item => getTimeLabel(item, 'profit')),
      datasets: [
        {
          label: 'Lợi nhuận',
          data: data.map(item => item.profit),
          borderColor: '#F59E0B',
          backgroundColor: 'rgba(245, 158, 11, 0.1)',
          borderWidth: 3,
          fill: true,
          tension: 0.4,
          pointBackgroundColor: '#F59E0B',
          pointBorderColor: '#fff',
          pointBorderWidth: 2,
          pointRadius: 6,
          pointHoverRadius: 8,
        },
      ],
    };
  };

  // Create combined chart data with all three metrics
  const getCombinedChartData = () => {
    const revenueData = getCurrentRevenueData();
    const ordersData = getCurrentOrdersData();
    const profitData = getCurrentProfitData();
    
    // Use the same labels (assuming all data have the same time periods)
    const labels = revenueData.map(item => getTimeLabel(item, 'revenue'));
    
    return {
      labels: labels,
      datasets: [
        {
          label: 'Doanh thu',
          data: revenueData.map(item => item.revenue),
          borderColor: '#10B981',
          backgroundColor: 'rgba(16, 185, 129, 0.1)',
          borderWidth: 3,
          fill: false,
          tension: 0.4,
          pointBackgroundColor: '#10B981',
          pointBorderColor: '#fff',
          pointBorderWidth: 2,
          pointRadius: 4,
          pointHoverRadius: 6,
          yAxisID: 'y',
        },
        {
          label: 'Đơn hàng',
          data: ordersData.map(item => item.orders),
          borderColor: '#8B5CF6',
          backgroundColor: 'rgba(139, 92, 246, 0.1)',
          borderWidth: 3,
          fill: false,
          tension: 0.4,
          pointBackgroundColor: '#8B5CF6',
          pointBorderColor: '#fff',
          pointBorderWidth: 2,
          pointRadius: 4,
          pointHoverRadius: 6,
          yAxisID: 'y1',
        },
        {
          label: 'Lợi nhuận',
          data: profitData.map(item => item.profit),
          borderColor: '#F59E0B',
          backgroundColor: 'rgba(245, 158, 11, 0.1)',
          borderWidth: 3,
          fill: false,
          tension: 0.4,
          pointBackgroundColor: '#F59E0B',
          pointBorderColor: '#fff',
          pointBorderWidth: 2,
          pointRadius: 4,
          pointHoverRadius: 6,
          yAxisID: 'y',
        },
      ],
    };
  };

  return (
    <>
      <style>{customDatePickerStyles}</style>
    <div className="space-y-6 dashboard-container">
      {/* Header */}
      <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center space-y-4 lg:space-y-0">
        <div>
          <h1 className="text-2xl lg:text-3xl font-bold text-gray-900">Dashboard</h1>
          <p className="text-sm lg:text-base text-gray-600 mt-1">Tổng quan hệ thống và thống kê chi tiết</p>
        </div>
        <div className="flex flex-col lg:flex-row items-center space-y-2 lg:space-y-0 lg:space-x-3 w-full lg:w-auto">
          {/* Time Range Selector */}
          <div className="flex flex-col lg:flex-row items-center space-y-2 lg:space-y-0 lg:space-x-2 w-full lg:w-auto">
          <select
            value={timeRange}
              onChange={(e) => handleTimeRangeChange(e.target.value)}
              className="w-full lg:w-auto px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="daily">Theo ngày</option>
              <option value="weekly">Theo tuần</option>
              <option value="monthly">Theo tháng</option>
              <option value="yearly">Theo năm</option>
              <option value="custom">Tùy chỉnh</option>
          </select>
            
            {/* Custom Date Range Button */}
            <button
              onClick={() => setShowCustomDatePicker(!showCustomDatePicker)}
              className={`w-full lg:w-auto px-3 py-2 text-sm border rounded-lg transition-colors ${
                showCustomDatePicker 
                  ? 'border-blue-500 bg-blue-50 text-blue-600' 
                  : 'border-gray-300 hover:border-blue-500 hover:bg-blue-50'
              }`}
            >
              <Calendar className="w-4 h-4" />
            </button>
          </div>

          {/* Custom Date Range Picker Modal */}
          {showCustomDatePicker && (
            <>
              {/* Backdrop */}
              <div 
                className="fixed inset-0 bg-black bg-opacity-50 z-40"
                onClick={() => setShowCustomDatePicker(false)}
              ></div>
              
                      {/* Modal */}
        <div className="fixed inset-0 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl p-8 max-w-md w-full mx-4">
                  <div className="text-center mb-6">
                    <h3 className="text-xl font-bold text-gray-900 mb-2">Chọn khoảng thời gian</h3>
                    <p className="text-base text-gray-600">Chọn từ ngày đến ngày (tối đa 31 ngày)</p>
                  </div>
                  
                  <div className="space-y-6">
                    <div>
                      <label className="block text-lg font-medium text-gray-700 mb-3">Từ ngày</label>
                      <DatePicker
                        selected={startDate}
                        onChange={(date) => setStartDate(date)}
                        selectsStart
                        startDate={startDate}
                        endDate={endDate}
                        maxDate={endDate}
                        dateFormat="dd/MM/yyyy"
                        className="w-full px-4 py-3 text-lg border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        calendarClassName="text-lg"
                        popperClassName="text-lg"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-lg font-medium text-gray-700 mb-3">Đến ngày</label>
                      <DatePicker
                        selected={endDate}
                        onChange={(date) => setEndDate(date)}
                        selectsEnd
                        startDate={startDate}
                        endDate={endDate}
                        minDate={startDate}
                        maxDate={new Date()}
                        dateFormat="dd/MM/yyyy"
                        className="w-full px-4 py-3 text-lg border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        calendarClassName="text-lg"
                        popperClassName="text-lg"
                      />
                    </div>
                    
                    {/* Date Range Info */}
                    <div className="bg-blue-50 p-4 rounded-lg">
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-gray-600">Khoảng thời gian:</span>
                        <span className="font-medium text-blue-600">
                          {Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24))} ngày
                        </span>
                      </div>
                      <div className="text-xs text-gray-500 mt-1">
                        {validateDateRange(startDate, endDate) 
                          ? '✅ Khoảng thời gian hợp lệ' 
                          : '❌ Khoảng thời gian không được vượt quá 31 ngày'
                        }
                      </div>
                    </div>
                    
                    <div className="flex flex-col lg:flex-row space-y-2 lg:space-y-0 lg:space-x-3 pt-4">
                      <button
                        onClick={handleCustomDateRange}
                        disabled={!validateDateRange(startDate, endDate)}
                        className={`flex-1 px-6 py-3 text-lg font-medium rounded-lg transition-colors ${
                          validateDateRange(startDate, endDate)
                            ? 'bg-blue-600 text-white hover:bg-blue-700'
                            : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                        }`}
                      >
                        Áp dụng
                      </button>
                      <button
                        onClick={() => setShowCustomDatePicker(false)}
                        className="flex-1 px-6 py-3 text-lg font-medium border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                      >
                        Hủy
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}

          {/* Action Buttons */}
          <div className="flex flex-col lg:flex-row items-center space-y-2 lg:space-y-0 lg:space-x-2 w-full lg:w-auto">
            <button 
              onClick={() => handleTimeRangeChange(timeRange)}
              className="w-full lg:w-auto p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              title="Làm mới dữ liệu"
            >
            <RefreshCw className="w-5 h-5" />
          </button>
            <button
              onClick={exportReport}
              disabled={isExporting}
              className={`w-full lg:w-auto flex items-center justify-center space-x-2 px-4 py-2 text-sm rounded-lg transition-colors ${
                isExporting
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  : 'bg-green-600 text-white hover:bg-green-700'
              }`}
            >
              {isExporting ? (
                <RefreshCw className="w-4 h-4 animate-spin" />
              ) : (
                <FileText className="w-4 h-4" />
              )}
              <span>{isExporting ? 'Đang xuất...' : 'Xuất báo cáo'}</span>
            </button>
          </div>
        </div>
      </div>

      {/* Stats Cards - Responsive Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 stats-cards">
        <div className="bg-gradient-to-br from-blue-500 to-blue-600 p-4 lg:p-6 rounded-xl shadow-lg text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-blue-100 text-xs lg:text-sm font-medium">Tổng người dùng</p>
              <p className="text-xl lg:text-3xl font-bold">{getStatsByTimeRange(timeRange).totalUsers.toLocaleString()}</p>
              <p className="text-blue-200 text-xs lg:text-sm">+{getStatsByTimeRange(timeRange).newUsers} mới</p>
            </div>
            <div className="p-2 lg:p-3 bg-blue-400 rounded-lg">
              <Users className="w-6 h-6 lg:w-8 lg:h-8" />
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-green-500 to-green-600 p-4 lg:p-6 rounded-xl shadow-lg text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-green-100 text-xs lg:text-sm font-medium">Doanh thu</p>
              <p className="text-xl lg:text-3xl font-bold">{formatCurrency(getStatsByTimeRange(timeRange).revenue)}</p>
              <p className="text-green-200 text-xs lg:text-sm">+15.2%</p>
            </div>
            <div className="p-2 lg:p-3 bg-green-400 rounded-lg">
              <DollarSign className="w-6 h-6 lg:w-8 lg:h-8" />
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-purple-500 to-purple-600 p-4 lg:p-6 rounded-xl shadow-lg text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-purple-100 text-xs lg:text-sm font-medium">Đơn hàng</p>
              <p className="text-xl lg:text-3xl font-bold">{getStatsByTimeRange(timeRange).orders}</p>
              <p className="text-purple-200 text-xs lg:text-sm">+8.5%</p>
            </div>
            <div className="p-2 lg:p-3 bg-purple-400 rounded-lg">
              <ShoppingCart className="w-6 h-6 lg:w-8 lg:h-8" />
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-orange-500 to-orange-600 p-4 lg:p-6 rounded-xl shadow-lg text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-orange-100 text-xs lg:text-sm font-medium">Lợi nhuận</p>
              <p className="text-xl lg:text-3xl font-bold">{formatCurrency(getStatsByTimeRange(timeRange).profit)}</p>
              <p className="text-orange-200 text-xs lg:text-sm">+{getStatsByTimeRange(timeRange).growth}%</p>
            </div>
            <div className="p-2 lg:p-3 bg-orange-400 rounded-lg">
              <TrendingUp className="w-6 h-6 lg:w-8 lg:h-8" />
            </div>
          </div>
        </div>
      </div>

      {/* Combined Development Chart */}
        <div className={`bg-white p-4 lg:p-6 rounded-xl shadow-lg border chart-section ${isChartFullscreen ? 'chart-fullscreen' : ''}`}>
          <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between mb-4 lg:mb-6">
            <div className="mb-4 lg:mb-0">
              <h3 className="text-lg lg:text-xl font-semibold text-gray-900">Phát triển tổng quan</h3>
              <p className="text-sm text-gray-600 mt-1">
                {timeRange === 'custom' 
                  ? `${startDate.toLocaleDateString()} - ${endDate.toLocaleDateString()}`
                  : timeRange === 'daily' ? 'Theo ngày'
                  : timeRange === 'weekly' ? 'Theo tuần'
                  : timeRange === 'monthly' ? 'Theo tháng'
                  : timeRange === 'yearly' ? 'Theo năm'
                  : 'Tùy chỉnh'
                }
              </p>
            </div>
            <div className="flex items-center space-x-2">
              <span className="text-xs lg:text-sm text-gray-600">
                <span className="inline-block w-3 h-3 bg-green-500 rounded-full mr-1"></span>
                Doanh thu
              </span>
              <span className="text-xs lg:text-sm text-gray-600">
                <span className="inline-block w-3 h-3 bg-purple-500 rounded-full mr-1"></span>
                Đơn hàng
              </span>
              <span className="text-xs lg:text-sm text-gray-600">
                <span className="inline-block w-3 h-3 bg-orange-500 rounded-full mr-1"></span>
                Lợi nhuận
              </span>
              <button
                onClick={() => setIsChartFullscreen(!isChartFullscreen)}
                className="p-1.5 lg:p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                title={isChartFullscreen ? "Thu nhỏ biểu đồ" : "Phóng to biểu đồ"}
              >
                <Eye className="w-4 h-4 lg:w-5 lg:h-5" />
              </button>
            </div>
          </div>
          
          {/* Chart Container - 90% height for small laptop screens */}
          <div className={`chart-container chart-wrapper w-full transition-all duration-300 ${
            isChartFullscreen 
              ? 'h-[90vh]' 
              : 'h-64 sm:h-80 lg:h-[90vh] xl:h-[90vh] 2xl:h-[90vh]'
          }`}>
            <Line data={getCombinedChartData()} options={combinedChartOptions} />
          </div>

          {/* Chart Summary - Responsive Grid */}
          <div className="mt-4 lg:mt-6 grid grid-cols-1 sm:grid-cols-3 gap-3 lg:gap-4">
            <div className="text-center p-3 lg:p-4 bg-green-50 rounded-lg">
              <div className="flex items-center justify-center mb-2">
                <div className="w-2 h-2 lg:w-3 lg:h-3 bg-green-500 rounded-full mr-2"></div>
                <p className="text-xs lg:text-sm font-medium text-gray-700">Doanh thu</p>
              </div>
              <p className="text-sm lg:text-lg font-bold text-green-600">
                {formatCurrency(getCurrentRevenueData().reduce((sum, item) => sum + item.revenue, 0))}
              </p>
              <p className="text-xs lg:text-sm text-green-600">
                +{((getCurrentRevenueData()[getCurrentRevenueData().length - 1]?.growth || 0)).toFixed(1)}%
              </p>
            </div>
            
            <div className="text-center p-3 lg:p-4 bg-purple-50 rounded-lg">
              <div className="flex items-center justify-center mb-2">
                <div className="w-2 h-2 lg:w-3 lg:h-3 bg-purple-500 rounded-full mr-2"></div>
                <p className="text-xs lg:text-sm font-medium text-gray-700">Đơn hàng</p>
              </div>
              <p className="text-sm lg:text-lg font-bold text-purple-600">
                {getCurrentOrdersData().reduce((sum, item) => sum + item.orders, 0)}
              </p>
              <p className="text-xs lg:text-sm text-purple-600">
                +{((getCurrentOrdersData()[getCurrentOrdersData().length - 1]?.growth || 0)).toFixed(1)}%
              </p>
            </div>
            
            <div className="text-center p-3 lg:p-4 bg-orange-50 rounded-lg">
              <div className="flex items-center justify-center mb-2">
                <div className="w-2 h-2 lg:w-3 lg:h-3 bg-orange-500 rounded-full mr-2"></div>
                <p className="text-xs lg:text-sm font-medium text-gray-700">Lợi nhuận</p>
              </div>
              <p className="text-sm lg:text-lg font-bold text-orange-600">
                {formatCurrency(getCurrentProfitData().reduce((sum, item) => sum + item.profit, 0))}
              </p>
              <p className="text-xs lg:text-sm text-orange-600">
                +{((getCurrentProfitData()[getCurrentProfitData().length - 1]?.growth || 0)).toFixed(1)}%
              </p>
            </div>
          </div>
        </div>

      {/* Secondary Charts - Responsive Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-6 secondary-charts">
        {/* Growth Chart */}
        <div className="bg-white p-4 lg:p-6 rounded-xl shadow-lg border">
          <div className="flex items-center justify-between mb-4 lg:mb-6">
            <h3 className="text-base lg:text-lg font-semibold text-gray-900">Tốc độ phát triển</h3>
            <div className="flex items-center space-x-2">
              <select
                value={selectedChart}
                onChange={(e) => setSelectedChart(e.target.value)}
                className="px-2 lg:px-3 py-1 lg:py-2 text-xs lg:text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="users">Người dùng</option>
                <option value="revenue">Doanh thu</option>
                <option value="orders">Đơn hàng</option>
                <option value="profit">Lợi nhuận</option>
              </select>
            </div>
          </div>
          
          <div className="space-y-3 lg:space-y-4">
            {chartData.map((data, index) => (
              <div key={index} className="flex items-center justify-between">
                <span className="text-xs lg:text-sm font-medium text-gray-600 w-8 lg:w-12">{data.month}</span>
                <div className="flex-1 mx-2 lg:mx-4">
                  <div className="w-full bg-gray-200 rounded-full h-2 lg:h-3">
                    <div 
                      className={`h-2 lg:h-3 rounded-full transition-all duration-500 ${
                        selectedChart === 'users' 
                          ? 'bg-blue-500' 
                          : selectedChart === 'revenue' 
                          ? 'bg-green-500' 
                          : selectedChart === 'orders'
                          ? 'bg-purple-500'
                          : 'bg-orange-500'
                      }`}
                      style={{ 
                        width: `${selectedChart === 'users' 
                          ? (data.users / 300) * 100 
                          : selectedChart === 'revenue' 
                          ? (data.revenue / 2500000) * 100 
                          : selectedChart === 'orders'
                          ? (data.orders / 60) * 100
                          : (data.profit / 600000) * 100}%` 
                      }}
                    ></div>
                  </div>
                </div>
                <span className="text-xs lg:text-sm font-medium text-gray-900 w-16 lg:w-20 text-right">
                  {selectedChart === 'users' 
                    ? data.users 
                    : selectedChart === 'revenue' 
                    ? formatCurrency(data.revenue)
                    : selectedChart === 'orders'
                    ? data.orders
                    : formatCurrency(data.profit)}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Financial Summary */}
        <div className="bg-white p-4 lg:p-6 rounded-xl shadow-lg border">
          <div className="flex items-center justify-between mb-4 lg:mb-6">
            <h3 className="text-base lg:text-lg font-semibold text-gray-900">Tóm tắt tài chính</h3>
            <Calendar className="w-4 h-4 lg:w-5 lg:h-5 text-gray-400" />
          </div>
          
          <div className="space-y-3 lg:space-y-4">
            <div className="flex items-center justify-between p-3 lg:p-4 bg-green-50 rounded-lg">
              <div className="flex items-center space-x-2 lg:space-x-3">
                <div className="p-1.5 lg:p-2 bg-green-100 rounded-lg">
                  <DollarSign className="w-4 h-4 lg:w-5 lg:h-5 text-green-600" />
                </div>
                <div>
                  <p className="text-sm lg:text-base font-medium text-gray-900">Doanh thu</p>
                  <p className="text-xs lg:text-sm text-gray-600">Tổng thu nhập</p>
                </div>
              </div>
              <p className="text-sm lg:text-lg font-bold text-green-600">{formatCurrency(getStatsByTimeRange(timeRange).revenue)}</p>
            </div>

            <div className="flex items-center justify-between p-3 lg:p-4 bg-red-50 rounded-lg">
              <div className="flex items-center space-x-2 lg:space-x-3">
                <div className="p-1.5 lg:p-2 bg-red-100 rounded-lg">
                  <TrendingUp className="w-4 h-4 lg:w-5 lg:h-5 text-red-600" />
                </div>
                <div>
                  <p className="text-sm lg:text-base font-medium text-gray-900">Chi phí</p>
                  <p className="text-xs lg:text-sm text-gray-600">Tổng chi tiêu</p>
                </div>
              </div>
              <p className="text-sm lg:text-lg font-bold text-red-600">{formatCurrency(getStatsByTimeRange(timeRange).expenses)}</p>
            </div>

            <div className="flex items-center justify-between p-3 lg:p-4 bg-blue-50 rounded-lg">
              <div className="flex items-center space-x-2 lg:space-x-3">
                <div className="p-1.5 lg:p-2 bg-blue-100 rounded-lg">
                  <BarChart3 className="w-4 h-4 lg:w-5 lg:h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm lg:text-base font-medium text-gray-900">Lợi nhuận</p>
                  <p className="text-xs lg:text-sm text-gray-600">Thu nhập ròng</p>
                </div>
              </div>
              <p className="text-sm lg:text-lg font-bold text-blue-600">{formatCurrency(getStatsByTimeRange(timeRange).profit)}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Top Products and Sellers - Responsive Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-6">
        {/* Top Products */}
        <div className="bg-white p-4 lg:p-6 rounded-xl shadow-lg border">
          <div className="flex items-center justify-between mb-4 lg:mb-6">
            <h3 className="text-base lg:text-lg font-semibold text-gray-900">Top 5 sản phẩm bán chạy</h3>
            <button className="p-1.5 lg:p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors">
              <Download className="w-4 h-4 lg:w-5 lg:h-5" />
            </button>
          </div>
          
          <div className="space-y-3 lg:space-y-4">
            {getTopProductsByTimeRange(timeRange).map((product, index) => (
              <div 
                key={index} 
                className="p-3 lg:p-4 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100 transition-colors"
                onClick={() => handleProductClick(product)}
              >
                <div className="flex items-center justify-between mb-2 lg:mb-3">
                  <div className="flex items-center space-x-2 lg:space-x-3">
                    <div className={`w-6 h-6 lg:w-8 lg:h-8 rounded-full flex items-center justify-center text-white font-bold text-xs lg:text-sm ${
                      index === 0 ? 'bg-yellow-500' : 
                      index === 1 ? 'bg-gray-400' : 
                      index === 2 ? 'bg-orange-500' : 'bg-blue-500'
                    }`}>
                      {index + 1}
                    </div>
                    <div>
                      <p className="text-sm lg:text-base font-medium text-gray-900">{product.name}</p>
                      <p className="text-xs lg:text-sm text-gray-600">{product.sales} đã bán</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-sm lg:text-base font-bold text-gray-900">{formatCurrency(product.revenue)}</p>
                    <div className="flex items-center space-x-1">
                      {getGrowthIcon(product.growth)}
                      <span className={`text-xs lg:text-sm ${getGrowthColor(product.growth)}`}>
                        {product.growth > 0 ? '+' : ''}{product.growth}%
                      </span>
                    </div>
                  </div>
                </div>
                
                {/* Mini Chart */}
                <div className="flex items-center space-x-2">
                  <div className="flex-1 h-1.5 lg:h-2 bg-gray-200 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-gradient-to-r from-blue-500 to-purple-500 rounded-full"
                      style={{ width: `${(product.sales / 150) * 100}%` }}
                    ></div>
                  </div>
                  <span className="text-xs text-gray-500">{product.sales} sales</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Top Sellers */}
        <div className="bg-white p-4 lg:p-6 rounded-xl shadow-lg border">
          <div className="flex items-center justify-between mb-4 lg:mb-6">
            <h3 className="text-base lg:text-lg font-semibold text-gray-900">Top 5 seller doanh thu cao</h3>
            <button className="p-1.5 lg:p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors">
              <Eye className="w-4 h-4 lg:w-5 lg:h-5" />
            </button>
          </div>
          
          <div className="space-y-3 lg:space-y-4">
            {getTopSellersByTimeRange(timeRange).map((seller, index) => (
              <div 
                key={index} 
                className="p-3 lg:p-4 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100 transition-colors"
                onClick={() => handleSellerClick(seller)}
              >
                <div className="flex items-center justify-between mb-2 lg:mb-3">
                  <div className="flex items-center space-x-2 lg:space-x-3">
                    <div className={`w-6 h-6 lg:w-8 lg:h-8 rounded-full flex items-center justify-center text-white font-bold text-xs lg:text-sm ${
                      index === 0 ? 'bg-yellow-500' : 
                      index === 1 ? 'bg-gray-400' : 
                      index === 2 ? 'bg-orange-500' : 'bg-blue-500'
                    }`}>
                      {index + 1}
                    </div>
                    <div>
                      <p className="text-sm lg:text-base font-medium text-gray-900">{seller.name}</p>
                      <p className="text-xs lg:text-sm text-gray-600">{seller.orders} đơn hàng</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-sm lg:text-base font-bold text-gray-900">{formatCurrency(seller.revenue)}</p>
                    <div className="flex items-center space-x-1">
                      {getGrowthIcon(seller.growth)}
                      <span className={`text-xs lg:text-sm ${getGrowthColor(seller.growth)}`}>
                        {seller.growth > 0 ? '+' : ''}{seller.growth}%
                      </span>
                    </div>
                  </div>
                </div>
                
                {/* Mini Chart */}
                <div className="flex items-center space-x-2">
                  <div className="flex-1 h-1.5 lg:h-2 bg-gray-200 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-gradient-to-r from-green-500 to-blue-500 rounded-full"
                      style={{ width: `${(seller.revenue / 3500000) * 100}%` }}
                    ></div>
                  </div>
                  <span className="text-xs text-gray-500">{seller.orders} orders</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="bg-gradient-to-r from-blue-500 to-purple-600 p-4 lg:p-6 rounded-xl shadow-lg text-white">
        <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between space-y-4 lg:space-y-0">
          <div>
            <h3 className="text-lg lg:text-xl font-bold mb-2">Cần hỗ trợ?</h3>
            <p className="text-sm lg:text-base text-blue-100">Xem báo cáo chi tiết hoặc liên hệ hỗ trợ</p>
          </div>
          <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-3">
            <button className="px-4 py-2 bg-white bg-opacity-20 hover:bg-opacity-30 rounded-lg transition-colors text-sm lg:text-base">
              Xem báo cáo
            </button>
            <button className="px-4 py-2 bg-white text-blue-600 hover:bg-gray-100 rounded-lg transition-colors text-sm lg:text-base">
              Liên hệ hỗ trợ
            </button>
          </div>
        </div>
      </div>
    </div>

    {/* Product Detail Popup */}
    {showProductPopup && selectedProduct && (
      <>
        {/* Backdrop */}
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-50"
          onClick={closeProductPopup}
        ></div>
        
        {/* Modal */}
        <div className="fixed inset-0 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <div className="flex items-center space-x-3">
                <Package className="w-6 h-6 text-blue-600" />
                <h3 className="text-xl font-bold text-gray-900">Chi tiết sản phẩm</h3>
              </div>
              <button
                onClick={closeProductPopup}
                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Content */}
            <div className="p-6 space-y-6">
              {/* Product Image and Basic Info */}
              <div className="flex flex-col lg:flex-row space-y-4 lg:space-y-0 lg:space-x-6">
                <div className="lg:w-1/3">
                  <div className="bg-gray-100 rounded-lg p-4 flex items-center justify-center">
                    <img
                      src={selectedProduct.image || 'https://via.placeholder.com/200x200?text=Product'}
                      alt={selectedProduct.name}
                      className="w-full h-48 object-cover rounded-lg"
                    />
                  </div>
                </div>
                
                <div className="lg:w-2/3 space-y-4">
                  <div>
                    <h4 className="text-lg font-bold text-gray-900 mb-2">{selectedProduct.name}</h4>
                    <p className="text-gray-600">{selectedProduct.description || 'Không có mô tả'}</p>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-blue-50 p-3 rounded-lg">
                      <div className="flex items-center space-x-2">
                        <Tag className="w-4 h-4 text-blue-600" />
                        <span className="text-sm font-medium text-gray-700">Giá</span>
                      </div>
                      <p className="text-lg font-bold text-blue-600">{formatCurrency(selectedProduct.price)}</p>
                    </div>
                    
                    <div className="bg-green-50 p-3 rounded-lg">
                      <div className="flex items-center space-x-2">
                        <ShoppingCart className="w-4 h-4 text-green-600" />
                        <span className="text-sm font-medium text-gray-700">Đã bán</span>
                      </div>
                      <p className="text-lg font-bold text-green-600">{selectedProduct.sales}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Financial Summary */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h5 className="font-semibold text-gray-900 mb-4">Tóm tắt tài chính ({timeRange})</h5>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="bg-white p-3 rounded-lg">
                    <div className="flex items-center space-x-2 mb-1">
                      <DollarSign className="w-4 h-4 text-green-600" />
                      <span className="text-sm font-medium text-gray-700">Doanh thu</span>
                    </div>
                    <p className="text-lg font-bold text-green-600">{formatCurrency(selectedProduct.revenue)}</p>
                    <div className="flex items-center space-x-1 mt-1">
                      {getGrowthIcon(selectedProduct.growth)}
                      <span className={`text-sm ${getGrowthColor(selectedProduct.growth)}`}>
                        {selectedProduct.growth > 0 ? '+' : ''}{selectedProduct.growth}%
                      </span>
                    </div>
                  </div>
                  
                  <div className="bg-white p-3 rounded-lg">
                    <div className="flex items-center space-x-2 mb-1">
                      <TrendingUp className="w-4 h-4 text-red-600" />
                      <span className="text-sm font-medium text-gray-700">Chi phí</span>
                    </div>
                    <p className="text-lg font-bold text-red-600">{formatCurrency(selectedProduct.cost || selectedProduct.revenue * 0.6)}</p>
                  </div>
                  
                  <div className="bg-white p-3 rounded-lg">
                    <div className="flex items-center space-x-2 mb-1">
                      <BarChart3 className="w-4 h-4 text-blue-600" />
                      <span className="text-sm font-medium text-gray-700">Lợi nhuận</span>
                    </div>
                    <p className="text-lg font-bold text-blue-600">{formatCurrency(selectedProduct.profit || selectedProduct.revenue * 0.4)}</p>
                  </div>
                </div>
              </div>

              {/* Additional Info */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-white border border-gray-200 rounded-lg p-4">
                  <h6 className="font-semibold text-gray-900 mb-3 flex items-center">
                    <Clock className="w-4 h-4 mr-2" />
                    Thông tin thời gian
                  </h6>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Ngày đăng:</span>
                      <span className="font-medium">{selectedProduct.createdAt || '01/01/2024'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Cập nhật cuối:</span>
                      <span className="font-medium">{selectedProduct.updatedAt || '15/01/2024'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Trạng thái:</span>
                      <span className="font-medium text-green-600">Đang bán</span>
                    </div>
                  </div>
                </div>
                
                <div className="bg-white border border-gray-200 rounded-lg p-4">
                  <h6 className="font-semibold text-gray-900 mb-3 flex items-center">
                    <Star className="w-4 h-4 mr-2" />
                    Đánh giá & Phân loại
                  </h6>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Đánh giá:</span>
                      <span className="font-medium">4.5/5 ⭐</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Danh mục:</span>
                      <span className="font-medium">{selectedProduct.category || 'Điện tử'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Thương hiệu:</span>
                      <span className="font-medium">{selectedProduct.brand || 'Unknown'}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </>
    )}

    {/* Seller Detail Popup */}
    {showSellerPopup && selectedSeller && (
      <>
        {/* Backdrop */}
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-50"
          onClick={closeSellerPopup}
        ></div>
        
        {/* Modal */}
        <div className="fixed inset-0 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <div className="flex items-center space-x-3">
                <User className="w-6 h-6 text-green-600" />
                <h3 className="text-xl font-bold text-gray-900">Chi tiết Seller</h3>
              </div>
              <button
                onClick={closeSellerPopup}
                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Content */}
            <div className="p-6 space-y-6">
              {/* Seller Profile */}
              <div className="flex items-center space-x-4">
                <div className="w-16 h-16 bg-gradient-to-r from-green-400 to-blue-500 rounded-full flex items-center justify-center">
                  <span className="text-white font-bold text-lg">
                    {selectedSeller.name.charAt(0).toUpperCase()}
                  </span>
                </div>
                <div>
                  <h4 className="text-lg font-bold text-gray-900">{selectedSeller.name}</h4>
                  <p className="text-gray-600">{selectedSeller.email || 'seller@example.com'}</p>
                  <div className="flex items-center space-x-2 mt-1">
                    <MapPin className="w-4 h-4 text-gray-400" />
                    <span className="text-sm text-gray-500">{selectedSeller.location || 'Hà Nội, Việt Nam'}</span>
                  </div>
                </div>
              </div>

              {/* Contact Info */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h5 className="font-semibold text-gray-900 mb-3">Thông tin liên hệ</h5>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div className="flex items-center space-x-2">
                    <Phone className="w-4 h-4 text-gray-400" />
                    <span className="text-sm">{selectedSeller.phone || '+84 123 456 789'}</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Mail className="w-4 h-4 text-gray-400" />
                    <span className="text-sm">{selectedSeller.email || 'seller@example.com'}</span>
                  </div>
                </div>
              </div>

              {/* Performance Summary */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h5 className="font-semibold text-gray-900 mb-4">Hiệu suất kinh doanh ({timeRange})</h5>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="bg-white p-3 rounded-lg">
                    <div className="flex items-center space-x-2 mb-1">
                      <ShoppingCart className="w-4 h-4 text-blue-600" />
                      <span className="text-sm font-medium text-gray-700">Đơn hàng</span>
                    </div>
                    <p className="text-lg font-bold text-blue-600">{selectedSeller.orders}</p>
                    <div className="flex items-center space-x-1 mt-1">
                      {getGrowthIcon(selectedSeller.growth)}
                      <span className={`text-sm ${getGrowthColor(selectedSeller.growth)}`}>
                        {selectedSeller.growth > 0 ? '+' : ''}{selectedSeller.growth}%
                      </span>
                    </div>
                  </div>
                  
                  <div className="bg-white p-3 rounded-lg">
                    <div className="flex items-center space-x-2 mb-1">
                      <DollarSign className="w-4 h-4 text-green-600" />
                      <span className="text-sm font-medium text-gray-700">Doanh thu</span>
                    </div>
                    <p className="text-lg font-bold text-green-600">{formatCurrency(selectedSeller.revenue)}</p>
                  </div>
                  
                  <div className="bg-white p-3 rounded-lg">
                    <div className="flex items-center space-x-2 mb-1">
                      <BarChart3 className="w-4 h-4 text-purple-600" />
                      <span className="text-sm font-medium text-gray-700">Lợi nhuận</span>
                    </div>
                    <p className="text-lg font-bold text-purple-600">{formatCurrency(selectedSeller.profit || selectedSeller.revenue * 0.3)}</p>
                  </div>
                </div>
              </div>

              {/* Additional Stats */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-white border border-gray-200 rounded-lg p-4">
                  <h6 className="font-semibold text-gray-900 mb-3">Thống kê chi tiết</h6>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Sản phẩm đang bán:</span>
                      <span className="font-medium">{selectedSeller.products || 25}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Đánh giá trung bình:</span>
                      <span className="font-medium">4.8/5 ⭐</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Thời gian hoạt động:</span>
                      <span className="font-medium">2 năm 3 tháng</span>
                    </div>
                  </div>
                </div>
                
                <div className="bg-white border border-gray-200 rounded-lg p-4">
                  <h6 className="font-semibold text-gray-900 mb-3">Trạng thái</h6>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Trạng thái:</span>
                      <span className="font-medium text-green-600">Đang hoạt động</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Xác thực:</span>
                      <span className="font-medium text-green-600">Đã xác thực</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Cấp độ:</span>
                      <span className="font-medium text-blue-600">Gold Seller</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </>
    )}
    </>
  );
};

export default AdminDashboard; 